import * as style from "./ApplicationList.module.scss";
import Typography from "../../Components/Common/CprimeText";
import React, { useEffect, useState } from "react";
import {
  Dropdown,
  TextArea,
  TextInput,
  DropdownType,
  Toggle,
  DropdownOption,
  Divider,
} from "cprime-foundational-components";
import {
  featureGroupListObject,
  saveFeatureData,
} from "../../store/slices/featureListSlice";
import { AppDispatch, RootState } from "../../store/store";
import { useSelector, useDispatch } from "react-redux";
import {
  getPermissionsList,
  getRoleSetupFeatureDropdownData,
} from "../../services/RequestAccess/RequestAccess";
import { triggerToast } from "../../utils/toast";

interface CreateFeaturePanelContentProps {
  getApplicationDetails: any;
  featureGrp: featureGroupListObject[];
  isEdit: boolean;
  appId: number;
}

const CreateFeaturePanelContent: React.FC<CreateFeaturePanelContentProps> = ({
  getApplicationDetails,
  featureGrp,
  isEdit,
  appId,
}) => {
  const scssClass = style as any;

  const editFeatureData = useSelector(
    (state: RootState) => state?.featureList?.editData,
  );

  const [toggleState, setToggleState] = useState(true);
  const [visibleToRoles, setVisibleToRoles] = useState<boolean>(true);
  const [featureName, setFeatureName] = useState<string>("");
  const [groupName, setGroupName] = useState<string[]>([]);
  const [featureGroups, setFeatureGroups] = useState<DropdownOption[]>([]);
  const [groupId, setGroupId] = useState<number[]>([]);
  const [featureDesc, setFeatureDesc] = useState<string>("");
  const [featurePermissions, setFeaturePermissions] = useState<
    DropdownOption[]
  >([]);
  const [dropdown, setDropdown] = useState<boolean>(false);
  const [permissionDropdown, setPermissionDropdown] = useState<boolean>(false);
  const [permissionId, setPermissionId] = useState<number[]>([]);
  const [rowId, setRowId] = useState<number>(0);
  const dispatch = useDispatch<AppDispatch>();

  const [dropdownSelected, setDropdownSelected] = useState<DropdownOption[]>(
    [],
  );
  const [selectedFeaturePermissions, setSelectedFeaturePermissions] = useState<
    DropdownOption[]
  >([]);
  const [associatedRoles, setAssociatedRoles] = useState<DropdownOption[]>([]);
  const [associatedRolesSelected, setAssociatedRolesSelected] = useState<
    DropdownOption[]
  >([]);

  // Fetch Associated Roles (using getRoleSetupFeatureDropdownData)
  useEffect(() => {
    const fetchAssociatedRoles = async () => {
      try {
        const response = await getRoleSetupFeatureDropdownData(appId);
        const associatedRolesData: any = response?.data?.associated_roles ?? [];
        const rolesArray = associatedRolesData.map((item: any) => ({
          id: item.id,
          label: item.label,
          group: item.group,
          value: `${item.id}`, // Unique value based on ID
        }));
        setAssociatedRoles(rolesArray);
      } catch (error) {
        triggerToast("Error fetching associated roles", "error");
      }
    };

    fetchAssociatedRoles();
  }, [appId]);

  useEffect(() => {
    if (featureGrp) {
      const group = featureGrp?.map((obj) => {
        return { id: obj?.group_id, label: obj?.group_name };
      });
      setFeatureGroups(group);
      //setAssociatedRoles(group);

      const fetchPermissions = async () => {
        let permission = [];
        try {
          const fetchPermissionData = await getPermissionsList(appId);
          const permissionData: any = fetchPermissionData?.data;
          permission = permissionData?.map((obj: any) => {
            return { id: obj?.id, label: obj?.permission_name };
          });
          if (editFeatureData?.permission_ids?.length > 0) {
            const filterIds = editFeatureData.permission_ids.map((id: any) =>
              Number(id),
            );
            const filteredData = permission.filter((item: any) =>
              filterIds.includes(item.id),
            );
            setPermissionId(filterIds);
            setSelectedFeaturePermissions(filteredData);
          }

          if (!isEdit) {
            const viewPermission = permission?.filter(
              (item: any) => item?.label?.toLowerCase() === "view",
            );
            setSelectedFeaturePermissions(viewPermission);
            setPermissionId([viewPermission[0]?.id]);
          }

          setFeaturePermissions(permission);
        } catch (error) {
          triggerToast("Error fetching permissions", "error");
        }
      };
      fetchPermissions();
    }
  }, [appId]);

  useEffect(() => {
    if (isEdit) {
      const selectedNames = editFeatureData?.group_name
        ?.split(",")
        ?.map((str: string) => str.trim());

      const matchedGroups = featureGrp?.filter((g) =>
        selectedNames?.includes(g.group_name),
      );

      const selected = matchedGroups?.map((g) => ({
        id: g.group_id,
        label: g.group_name,
      }));

      setDropdownSelected(selected);
      setGroupId(selected.map((s) => s.id));
      setGroupName(selected.map((s) => s.label));
      setFeatureDesc(editFeatureData?.feature_description);
      setFeatureName(editFeatureData?.feature_name);
      setRowId(editFeatureData?.id);
      const isOpenToAll = !!editFeatureData?.is_open_to_all;
      setVisibleToRoles(isOpenToAll);
      setToggleState(isOpenToAll);

      // role_ids → allowedRoles (associatedRolesSelected)
      if (!isOpenToAll && editFeatureData?.role_ids?.length > 0) {
        const roleIds = editFeatureData.role_ids.map((id: any) => Number(id));
        const selectedRoles = associatedRoles.filter((role) =>
          roleIds.includes(role.id as number),
        );
        setAssociatedRolesSelected(selectedRoles);
      } else {
        setAssociatedRolesSelected([]);
      }
    }
  }, [isEdit, editFeatureData, associatedRoles]);

  const saveFeature = () => {
    const saveBody = {
      id: rowId,
      feature_name: featureName,
      feature_description: featureDesc,
      group_id: groupId,
      appId: appId,
      group_name: groupName,
      permissionId: permissionId,
      allowedRoles: associatedRolesSelected.map((r) => r.id),
      visibleToRoles: visibleToRoles,
    };
    dispatch(saveFeatureData(saveBody));
    getApplicationDetails?.();
  };

  useEffect(() => {
    saveFeature();
  }, [
    featureDesc,
    featureName,
    groupId,
    permissionId,
    associatedRolesSelected,
    visibleToRoles,
  ]);

  const handleChange = (field: string, value: string) => {
    if (field === "featureName") setFeatureName(value);
    if (field === "featureDesc") setFeatureDesc(value);
  };

  const handleDropdownChange = (e: any, field: string) => {
    if (field === "groupName") {
      const ids = e.map((item: any) => Number(item.id));
      const labels = e.map((item: any) => item.label);

      setGroupId(ids);
      setGroupName(labels);
      setDropdownSelected(e);
    }
  };

  const handlePermissionDropdownChange = (e: any, field: string) => {
    if (field === "featurePermissions") {
      const ids = e?.map((item: any) => Number(item?.id));
      const selected = e?.map((item: any) => ({
        id: Number(item?.id),
        label: item?.label,
      }));

      if (
        selected.some(
          (item: { label: string }) => item?.label?.toLowerCase() === "view",
        )
      ) {
        setPermissionId(ids);
        setSelectedFeaturePermissions(selected);
      } else {
        setPermissionId([]);
        setSelectedFeaturePermissions(selected);
      }
    }
  };

  const handleAssociatedRolesChange = (selected: DropdownOption[]) => {
    if (selected?.length > associatedRoles?.length) {
      setAssociatedRolesSelected([]);
      return;
    }

    if (selected?.some((s) => s?.label === "All")) {
      setAssociatedRolesSelected(
        associatedRoles?.filter((r) => r?.label !== "All"),
      );
      return;
    }

    // Filter by ID to prevent duplicate label issues
    const uniqueSelected = selected?.filter(
      (item, index, self) => index === self.findIndex((t) => t.id === item.id),
    );

    setAssociatedRolesSelected(uniqueSelected);
  };

  return (
    <div className={scssClass.panelContent}>
      <Divider
        variant={"fullWidth"}
        sx={{
          marginBottom: "15px",
          width: "100%",
          marginLeft: "0px !important",
          backgroundColor: "#f8f8f9 !important",
        }}
      />
      <Typography className={scssClass.textDescription}>
        Feature Details
      </Typography>
      <div className={scssClass.fieldsContent}>
        <div className={scssClass.secondRow}>
          <TextInput
            value={featureName}
            width={"100%"}
            onChange={(e) => handleChange("featureName", e?.target?.value)}
            sx={{
              maxWidth: "100%",
              // "& .MuiInputBase-input": { padding: "0.75rem 1rem" },
              // "& .MuiInputBase-root": { height: "39.98px" },
            }}
            placeholder="Enter Name"
            label="Feature Name"
          />
        </div>
        <div className={scssClass.secondRow}>
          {/* Feature Group Dropdown */}
          <Dropdown
            open={dropdown}
            width={"100%"}
            selectedOptions={dropdownSelected}
            aria-label="feature-group"
            className={scssClass.textInputstyle}
            options={featureGroups}
            placeholder="Select Feature Groups"
            label="Feature Group (Optional)"
            optionsTextLimit={200}
            type={DropdownType.FilterMultiSelect}
            onChange={(e) => handleDropdownChange(e, "groupName")}
            onClose={() => setDropdown(false)}
            onOpen={() => setDropdown(true)}
          />
        </div>

        <div className={scssClass.secondRow}>
          <Dropdown
            open={permissionDropdown}
            width={"100%"}
            selectedOptions={selectedFeaturePermissions}
            className={scssClass.textInputstyle}
            options={featurePermissions}
            placeholder="Feature Permissions"
            aria-label="feature-permissions"
            label="Feature Permissions"
            optionsTextLimit={200}
            onChange={(e) =>
              handlePermissionDropdownChange(e, "featurePermissions")
            }
            onClose={() => setPermissionDropdown(false)}
            onOpen={() => setPermissionDropdown(true)}
            type={DropdownType.FilterMultiSelect}
            error={
              selectedFeaturePermissions?.length === 0 ||
              !selectedFeaturePermissions?.some(
                (item) => item?.label?.toLowerCase() === "view",
              )
            }
            errorMessage={
              selectedFeaturePermissions?.length === 0 ||
              !selectedFeaturePermissions?.some(
                (item) => item?.label?.toLowerCase() === "view",
              )
                ? "View permission is required"
                : ""
            }
          />
        </div>

        <div className={scssClass.secondRow}>
          <TextArea
            fullWidth
            placeholder="Enter Description"
            label="Description"
            value={featureDesc}
            onChange={(e) => handleChange("featureDesc", e?.target?.value)}
            maxLength={500}
          />
        </div>

        <div
          className={scssClass.secondRow}
          style={{ alignItems: "flex-start", flexDirection: "column" }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              width: "25%",
              color: "#58595B",
              fontSize: "12px",
            }}
          >
            Visible to All Roles
          </p>
          <Toggle
            onChange={() => {
              setToggleState(!toggleState);
              setVisibleToRoles(!visibleToRoles);
            }}
            sx={{
              overflow: "visible",
              marginBottom: "4px",
              "& .MuiSwitch-thumb": {
                marginTop: "4px",
              },
            }}
            checked={toggleState}
            toggledText={toggleState ? "On" : "Off"}
            size="large"
          />
        </div>

        {/* Associated Roles Dropdown */}
        {!toggleState && (
          <div className={scssClass.secondRow}>
            <Dropdown
              options={associatedRoles}
              selectedOptions={associatedRolesSelected}
              onChange={handleAssociatedRolesChange}
              placeholder="Select Allowed Roles"
              label="Roles Visible to"
              type={DropdownType.FilterMultiSelect}
              optionsTextLimit={200}
              width={"100%"}
              isOptionEqualToValue={(option: any, value: any) =>
                option?.id === value?.id
              }
              filterOptions={(options: any, state: any) => {
                const inputValue = state?.inputValue?.toLowerCase();
                if (!inputValue) return options;
                return options?.filter(
                  (option: any) =>
                    option?.label?.toLowerCase()?.includes(inputValue) ||
                    option?.group?.toLowerCase()?.includes(inputValue),
                );
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CreateFeaturePanelContent;
